import React from 'react';

import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import { type Reading, label, numeric, rowsFor } from '../ContributionMargin/liveModel';

import { config } from './config';
import type { MarketingPresentation } from './liveDataAdapter';
import { metricFormat, metricLabel, salesMetrics } from './marketingControls';
import {
  type Filters,
  LE_SERIES,
  type Row,
  dimensions,
  divide,
  format,
  seriesColour,
  value,
} from './presentationModel';
import { salesGmarShares } from './workflows';

const popMetrics = ['ROAS (Margin) (BR)', 'GM (BR)', 'Costs', 'CPC', 'Orders', 'CR (Orders/Sessions)'];
const popTableMetrics = [
  'Costs',
  'GM (BR)',
  'GM (AR)',
  'ROAS (Margin) (BR)',
  'ROAS (Margin) (AR)',
  'ROAS (New Buyer Margin) (BR)',
  'ROAS (Margin) (BR) (Cash only)',
  'Orders',
  'Sessions',
  'CPC',
  'CR (Orders/Sessions)',
  'Customer Discounts (BR)',
  'Customer Discounts (AR)',
  'GM (BR) After Customer Discount',
  'ROAS (GM (BR)) After Customer Discount',
];
function popChange(current: number | null, previous: number | null) {
  const ratio = divide(current, previous);
  return ratio === null ? null : ratio - 1;
}
export function salesReadings(rows: Array<Row>) {
  return salesGmarShares(rowsFor({ cardId: 'sales', status: 'ready', rows }, config.ratios, false, config.derived));
}
export function salesColumns(rows: Array<Reading>) {
  return [...new Set(rows.flatMap((row) => Object.keys(row)))].filter(
    (key) => !['split_one', 'split_two'].includes(key) && !/_(total|known)$/.test(key),
  );
}
export function salesColumnLabel(key: string) {
  return metricLabel(key) !== key
    ? metricLabel(key)
    : label(key.replace(/^sales_/, '')).replace(
        /\b(Br|Ar|All)\b/g,
        (suffix) => `(${suffix === 'All' ? suffix : suffix.toUpperCase()})`,
      );
}
export function salesColumnFormat(key: string) {
  return salesMetrics.some(([metric]) => metric === key)
    ? metricFormat(key)
    : key.endsWith('_share')
      ? 'percent'
      : key in config.ratios || /^(orders|customers|items)_/.test(key)
        ? 'number'
        : 'money';
}
export function salesTable(model: MarketingPresentation, filters: Filters) {
  const rows = salesReadings(filters.timeSplit === 'Date Cadence' ? model.salesDaily : model.sales),
    total = salesReadings(model.salesTotal);
  return {
    columns: salesColumns([...rows, ...total]),
    totalCount: total.length,
    rows: [...total.map((row) => ({ ...row, split_one: 'Grand Total', split_two: '' })), ...rows],
  };
}
export function popTable(model: MarketingPresentation, filters: Filters) {
  const shown = filters.measures === 'all' ? popTableMetrics : popMetrics;
  const groups = [...new Set([...model.summary, ...model.summaryPrevious].map((row) => String(row.split_one)))];
  const rowsOf = (rows: Array<Row>, group?: string) =>
    group === undefined ? rows : rows.filter((row) => String(row.split_one) === group);
  const ranked = groups.sort(
    (a, b) =>
      (value(rowsOf(model.summary, b), 'Costs') ?? 0) - (value(rowsOf(model.summary, a), 'Costs') ?? 0) ||
      (value(rowsOf(model.summaryPrevious, b), 'Costs') ?? 0) - (value(rowsOf(model.summaryPrevious, a), 'Costs') ?? 0),
  );
  return {
    metrics: shown,
    rows: [undefined, ...ranked].map((group) => ({
      group: group ?? 'Total',
      total: group === undefined,
      cells: shown.map((metric) => {
        const current = value(rowsOf(model.summary, group), metric),
          previous = value(rowsOf(model.summaryPrevious, group), metric);
        return { metric, current, previous, change: popChange(current, previous) };
      }),
    })),
  };
}
export function popRecords(model: MarketingPresentation, filters: Filters) {
  return popTable(model, filters).rows.map((row) => ({
    [dimensions[filters.split] ?? filters.split]: row.group,
    ...Object.fromEntries([
      ...row.cells.flatMap((cell) => [
        [`${cell.metric} CP`, cell.current],
        [`${cell.metric} PoP %`, cell.change],
      ]),
      ...row.cells.map((cell) => [`${cell.metric} PP`, cell.previous]),
    ]),
  }));
}
export function monthlyTable(model: MarketingPresentation) {
  const years = [...new Set(model.monthly.map((row) => String(row.p_date).slice(0, 4)))].sort();
  const months = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));
  const at = (year: string, month: string) => {
    const row = model.monthly.find((row) => String(row.p_date).slice(0, 7) === year + '-' + month);
    return numeric(
      rowsFor(
        { cardId: 'monthly', status: 'ready', rows: row ? [row] : [] },
        config.ratios,
        false,
        config.derived,
      )[0]?.[model.chartMetric],
    );
  };
  const rows = years.flatMap((year) => [
    { year, measure: metricLabel(model.chartMetric), growth: false, values: months.map((month) => at(year, month)) },
    {
      year,
      measure: 'YoY growth',
      growth: true,
      values: months.map((month) => {
        const current = at(year, month),
          prior = at(String(Number(year) - 1), month);
        return current === null || prior === null || prior === 0 ? null : current / prior - 1;
      }),
    },
  ]);
  return { years, months, at, rows };
}
export default function SupplementalViews({
  model,
  filters,
  onMeasures,
}: {
  model: MarketingPresentation;
  filters: Filters;
  onMeasures: (measures: string) => void;
}) {
  if (filters.view === 'PoP') {
    const bucket = (rows: Array<Row>, metric: string) => {
      const dates = [...new Set(rows.map((row) => String(row.bucket)))].sort();
      return dates.map((date) =>
        value(
          rows.filter((row) => row.bucket === date),
          metric,
        ),
      );
    };
    const table = popTable(model, filters);
    return (
      <>
        <section className="pm-tabular">
          <div className="pm-section-heading">
            <h2>Comparison table</h2>
            <div className="pm-measures">
              <button aria-pressed={filters.measures !== 'all'} onClick={() => onMeasures('core')}>
                Core measures
              </button>
              <button aria-pressed={filters.measures === 'all'} onClick={() => onMeasures('all')}>
                All {popTableMetrics.length} measures
              </button>
            </div>
            <span>
              {dimensions[filters.split] ?? filters.split} · CP {filters.from} to {filters.to} against the previous
              period
            </span>
          </div>
          <div className="pm-scroll" tabIndex={0} aria-label="Comparison table">
            <table>
              <thead>
                <tr>
                  <th rowSpan={2}>{dimensions[filters.split] ?? filters.split}</th>
                  {table.metrics.map((metric) => (
                    <th key={metric} colSpan={2}>
                      {metric}
                    </th>
                  ))}
                </tr>
                <tr>
                  {table.metrics.flatMap((metric) => [
                    <th key={metric + 'cp'}>CP</th>,
                    <th key={metric + 'pop'}>PoP %</th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr key={row.total ? 'total' : row.group} className={row.total ? 'pm-total' : undefined}>
                    <th>{row.group}</th>
                    {row.cells.flatMap(({ metric, current, previous, change }) => [
                      <td key={metric + 'cp'} title={`CP ${format(current, metric)} · PP ${format(previous, metric)}`}>
                        {format(current, metric, true)}
                      </td>,
                      <td key={metric + 'pop'}>{format(change, 'percent')}</td>,
                    ])}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="pm-paired-plots">
          {popMetrics.map((metric) => {
            const current = bucket(model.daily, metric),
              previous = bucket(model.previous, metric);
            const data = Array.from({ length: Math.max(current.length, previous.length) }, (_, index) => ({
              day: index + 1,
              current: current[index] ?? null,
              previous: previous[index] ?? null,
            }));
            return (
              <section className="pm-plot" key={metric}>
                <h2>{metric} · CP / PP</h2>
                <div className="pm-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                      <CartesianGrid vertical={false} stroke="#E3E7E2" />
                      <XAxis dataKey="day" />
                      <YAxis width={72} tickFormatter={(v) => format(v, metric, true)} />
                      <ReportChartTooltip formatter={(v) => format(typeof v === 'number' ? v : null, metric)} />
                      <Line
                        dataKey="current"
                        name="Current period"
                        stroke="#0F464F"
                        dot={false}
                        isAnimationActive={false}
                        connectNulls={false}
                      />
                      <Line
                        dataKey="previous"
                        name="Previous period"
                        stroke="#76BFAA"
                        strokeDasharray="5 3"
                        dot={false}
                        isAnimationActive={false}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="pm-source">
                  Period buckets align by position. Ratios use additive operands within each bucket; missing buckets
                  remain Unknown. Ordinary discount metrics retain their separately recovered BR/AR definitions.
                </p>
              </section>
            );
          })}
        </div>
      </>
    );
  }
  if (filters.view === 'Monthly') {
    const { years, months, at, rows } = monthlyTable(model);
    const data = months.map((month) => ({
      month,
      ...Object.fromEntries(years.map((year) => [year, at(year, month)])),
    }));
    return (
      <section className="pm-tabular">
        <h2>Performance YoY · {metricLabel(model.chartMetric)}</h2>
        <div className="pm-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid vertical={false} stroke="#E3E7E2" />
              <XAxis dataKey="month" />
              <YAxis width={72} />
              <ReportChartTooltip />
              {years.map((year, index) => (
                <Line
                  key={year}
                  dataKey={year}
                  stroke={index % 2 ? '#76BFAA' : '#0F464F'}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="pm-scroll">
          <table>
            <thead>
              <tr>
                <th>Year · measure</th>
                {months.map((month) => (
                  <th key={month}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.year + (row.growth ? '-growth' : '')}>
                  <th>
                    {row.year} · {row.measure}
                  </th>
                  {row.values.map((amount, index) => (
                    <td key={months[index]}>
                      {format(amount, row.growth ? 'percent' : metricFormat(model.chartMetric))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="pm-source">
          Fixed Last Click 30D Non-Passive attribution. Calendar months within the selected source window; current edge
          months may be partial. Missing months remain Unknown.
        </p>
      </section>
    );
  }
  const { rows, columns, totalCount } = salesTable(model, filters);
  const daily = rowsFor(
    { cardId: 'sales_daily', status: 'ready', rows: model.salesDaily },
    config.ratios,
    false,
    config.derived,
  );
  const splitKeys = [...new Set(daily.map((row) => JSON.stringify([row.split_one, row.split_two])))];
  const dates = [...new Set(daily.map((row) => String(row.bucket)))].sort();
  const salesCharts = [model.chartMetric, model.secondMetric].map((metric, chartIndex) => {
    const relative = chartIndex === 0 ? model.firstRelative : model.secondRelative;
    const displayFormat = relative ? 'percent' : metricFormat(metric);
    const points = dates.map((date) => {
      const values = splitKeys.map((key) =>
        numeric(
          daily.find((row) => row.bucket === date && JSON.stringify([row.split_one, row.split_two]) === key)?.[metric],
        ),
      );
      const denominator = values.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);
      return {
        date,
        ...Object.fromEntries(
          values.map((amount, index) => [
            'series' + index,
            relative ? (amount === null || !denominator ? null : amount / denominator) : amount,
          ]),
        ),
      };
    });
    return (
      <section className="pm-plot" key={chartIndex}>
        <h2>
          {metricLabel(metric)} · {relative ? 'relative' : 'absolute'}
        </h2>
        <div className="pm-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points}>
              <CartesianGrid vertical={false} stroke="var(--pm-rule)" />
              <XAxis dataKey="date" tick={{ fill: 'var(--pm-muted)' }} />
              <YAxis
                width={72}
                tick={{ fill: 'var(--pm-muted)' }}
                tickFormatter={(v) => format(v, displayFormat, true)}
              />
              <ReportChartTooltip
                formatter={(v) => format(typeof v === 'number' ? v : null, displayFormat)}
                itemStyle={{ color: 'inherit' }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                formatter={(name) => <span style={{ color: 'var(--pm-text)' }}>{name}</span>}
              />
              {splitKeys.slice(0, 24).map((key, index) => (
                <Line
                  key={key}
                  name={JSON.parse(key)
                    .map((split: string | null) => split ?? 'Unknown')
                    .filter((split: string, i: number) => i === 0 || split !== 'All')
                    .join(' · ')}
                  dataKey={'series' + index}
                  stroke={
                    index < LE_SERIES.length
                      ? filters.theme === 'dark'
                        ? `color-mix(in srgb, ${seriesColour(key, index, filters.theme)} 65%, var(--pm-text))`
                        : seriesColour(key, index, filters.theme)
                      : `hsl(${(index * 137.508) % 360}, 65%, ${filters.theme === 'dark' ? 70 : 38}%)`
                  }
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="pm-source">
          Each source split tuple is separate. Daily source COUNTD values are never summed into period totals. Up to 24
          groups plotted; every source group remains in the table and CSV.
        </p>
      </section>
    );
  });
  return (
    <section className="pm-tabular">
      <h2>Sales · source attribution groups</h2>
      <div className="pm-paired-plots">{salesCharts}</div>
      <p className="pm-source">
        analytics.sales_attribution · optional transaction bounds are Sydney civil timestamps; order bounds are UTC
        dates. Orders and customers below use their independent source COUNTD. Grouped distinct counts are never summed
        into the grand total. GM AR share uses the complete selected split population, separately for each period; the
        original Sales All PctTotal partition remains unverified.
      </p>
      <div className="pm-scroll" tabIndex={0} aria-label="Sales full table">
        <table>
          <thead>
            <tr>
              <th>Data Split 1</th>
              <th>Data Split 2</th>
              {columns.map((column) => (
                <th key={column}>{salesColumnLabel(column)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className={index < totalCount ? 'pm-total' : ''}>
                <th>{String(row.split_one ?? 'Unknown')}</th>
                <th>{String(row.split_two ?? 'Unknown')}</th>
                {columns.map((column) => (
                  <td key={column}>
                    {typeof row[column] === 'string'
                      ? String(row[column])
                      : format(numeric(row[column]), salesColumnFormat(column))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
